import React, { useState, useMemo, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import {
  Dropdown,
  DropdownMenu,
  DropdownToggle,
  InputGroup,
  InputGroupAddon,
  InputGroupText
} from 'reactstrap';
import styled from 'styled-components';

import { FlexControllerV2 } from 'components/FlexControllerInput/V2';
import MonitoringQueueGroupsManager from './MonitoringQueueGroupsManager';

const StyledDropdownMenu = styled(DropdownMenu)`
  width: 350px;
  padding: 0;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  overflow: hidden;
`;

const HeaderContainer = styled.div`
  padding: 12px 16px;
  border-bottom: 1px solid #f0f0f0;
  background-color: #fff;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ModeSelectorContainer = styled.div`
  display: flex;
  background-color: #f5f5f5;
  border-radius: 6px;
  padding: 4px;
  gap: 4px;
`;

const ModeLabel = styled.div`
  flex: 1;
  text-align: center;
  font-size: 0.8rem;
  font-weight: 600;
  padding: 8px 4px;
  margin: 0;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;
  user-select: none;

  /* Estilo Ativo vs Inativo */
  background-color: ${(props) => (props.active ? '#fff' : 'transparent')};
  color: ${(props) => (props.active ? '#333' : '#888')};
  box-shadow: ${(props) =>
    props.active ? '0 2px 4px rgba(0,0,0,0.05)' : 'none'};
  border: 1px solid ${(props) => (props.active ? '#e0e0e0' : 'transparent')};

  &:hover {
    color: #333;
  }
`;

const ScrollableContent = styled.div`
  max-height: 400px;
  overflow-y: auto;
  padding: 8px 0;

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background-color: #ccc;
    border-radius: 3px;
  }
`;

const SectionHeader = styled.div`
  padding: 12px 16px 8px;
  font-size: 0.75rem;
  font-weight: 700;
  color: #666;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const ListItem = styled.div`
  display: flex;
  align-items: center;
  padding: 8px 16px;
  cursor: pointer;
  transition: background 0.2s;
  font-size: 0.9rem;
  color: #333;

  &:hover {
    background-color: #f9f9f9;
  }
`;

const CheckboxSquare = styled.div`
  width: 18px;
  height: 18px;
  border: 1px solid ${(props) => (props.checked ? '#e91e63' : '#ccc')};
  background-color: ${(props) => (props.checked ? '#e91e63' : '#fff')};
  border-radius: 4px;
  margin-right: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all 0.2s;

  &:after {
    content: '';
    display: ${(props) => (props.checked ? 'block' : 'none')};
    width: 5px;
    height: 9px;
    border: solid white;
    border-width: 0 2px 2px 0;
    transform: rotate(45deg);
    margin-top: -2px;
  }
`;

const RadioCircle = styled.div`
  width: 18px;
  height: 18px;
  border: 2px solid ${(props) => (props.checked ? '#e91e63' : '#ccc')};
  background-color: #fff;
  border-radius: 50%;
  margin-right: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all 0.2s;

  &:after {
    content: '';
    display: ${(props) => (props.checked ? 'block' : 'none')};
    width: 8px;
    height: 8px;
    background-color: #e91e63;
    border-radius: 50%;
  }
`;

const Footer = styled.div`
  padding: 12px 16px;
  border-top: 1px solid #f0f0f0;
  background-color: #fafafa;
  cursor: pointer;
  display: flex;
  align-items: center;
  font-weight: 600;
  font-size: 0.9rem;
  color: #333;

  &:hover {
    background-color: #f0f0f0;
  }

  i {
    margin-right: 8px;
  }
`;

export const ViewModeSelector = ({
  groups,
  selectedGroup,
  onSelectGroup,
  onClearGroup,
  onGroupsChanged,
  manualSelection: rawManualSelection,
  onManualChange,
  allQueues = [],
  iconColor = 'inherit'
}) => {
  const manualSelection = rawManualSelection || [];
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isManagerModalOpen, setIsManagerModalOpen] = useState(false);

  const [viewMode, setViewMode] = useState('GROUPS');

  const { control, watch } = useForm({
    defaultValues: {
      search: ''
    }
  });

  const searchTerm = watch('search');

  useEffect(() => {
    if (selectedGroup) {
      setViewMode('GROUPS');
    } else if (manualSelection.length > 0) {
      setViewMode('QUEUES');
    }
  }, [selectedGroup, manualSelection.length]);

  const toggleDropdown = () => setIsDropdownOpen((prev) => !prev);
  const toggleManagerModal = () => setIsManagerModalOpen((prev) => !prev);

  const handleOpenManager = () => {
    setIsManagerModalOpen(true);
    setIsDropdownOpen(false);
  };

  const handleModeSwitch = (newMode, e) => {
    e.preventDefault();
    e.stopPropagation();
    setViewMode(newMode);
  };

  const filteredGroups = useMemo(() => {
    if (!searchTerm) return groups;
    return groups.filter((g) =>
      g.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [groups, searchTerm]);

  const filteredQueues = useMemo(() => {
    if (!searchTerm) return allQueues;
    return allQueues.filter((q) =>
      q.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [allQueues, searchTerm]);

  const handleGroupClick = (group) => {
    if (selectedGroup?.id === group.id) {
      if (onClearGroup) onClearGroup();
    } else {
      onSelectGroup(group);
    }
  };

  const handleQueueClick = (queue) => {
    const isSelected = manualSelection.some((q) => q.id === queue.id);
    let newSelection = [];

    if (isSelected) {
      newSelection = manualSelection.filter((q) => q.id !== queue.id);
    } else {
      newSelection = [...manualSelection, queue];
    }

    if (onManualChange) onManualChange(newSelection);
  };

  const isAllFilteredSelected =
    filteredQueues.length > 0 &&
    filteredQueues.every((q) => manualSelection.some((ms) => ms.id === q.id));

  const handleSelectAll = () => {
    if (isAllFilteredSelected) {
      const newSelection = manualSelection.filter(
        (ms) => !filteredQueues.some((fq) => fq.id === ms.id)
      );
      if (onManualChange) onManualChange(newSelection);
    } else {
      const newSelection = [...manualSelection];
      filteredQueues.forEach((fq) => {
        if (!newSelection.some((ms) => ms.id === fq.id)) {
          newSelection.push(fq);
        }
      });
      if (onManualChange) onManualChange(newSelection);
    }
  };

  let displayLabel = 'Selecione...';
  let displayIcon = 'fa-filter';

  const isManualMode = manualSelection && manualSelection.length > 0;
  const isAllSelected =
    isManualMode &&
    allQueues.length > 0 &&
    manualSelection.length === allQueues.length;

  if (selectedGroup) {
    displayLabel = selectedGroup.name;
    displayIcon = 'fa-layer-group';
  } else if (isAllSelected) {
    displayLabel = 'Todas as Filas';
    displayIcon = 'fa-globe';
  } else if (isManualMode) {
    displayLabel = `Filas (${manualSelection.length})`;
    displayIcon = 'fa-list-ul';
  } else {
    displayLabel = 'Selecione as filas';
  }

  return (
    <>
      <Dropdown
        isOpen={isDropdownOpen}
        toggle={toggleDropdown}
        direction="down"
      >
        <DropdownToggle
          tag="div"
          style={{ cursor: 'pointer', color: iconColor, fontWeight: 600 }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <i className={`fas ${displayIcon}`} />
            <span>{displayLabel}</span>
            <i className="fas fa-chevron-down" style={{ fontSize: '0.8rem' }} />
          </div>
        </DropdownToggle>

        <StyledDropdownMenu right>
          <HeaderContainer>
            <div style={{ width: '100%' }}>
              <InputGroup size="sm">
                <InputGroupAddon addonType="prepend">
                  <InputGroupText
                    style={{ backgroundColor: '#fff', borderRight: 'none' }}
                  >
                    <i className="fas fa-search text-muted" />
                  </InputGroupText>
                </InputGroupAddon>
                <div style={{ flex: 1 }}>
                  <FlexControllerV2
                    type="text"
                    name="search"
                    control={control}
                    placeholder="Buscar..."
                    style={{
                      boxShadow: 'none',
                      height: '31px',
                      fontSize: '0.9rem'
                    }}
                  />
                </div>
              </InputGroup>
            </div>

            <ModeSelectorContainer>
              <ModeLabel
                active={viewMode === 'GROUPS'}
                onClick={(e) => handleModeSwitch('GROUPS', e)}
              >
                Agrupamentos
              </ModeLabel>
              <ModeLabel
                active={viewMode === 'QUEUES'}
                onClick={(e) => handleModeSwitch('QUEUES', e)}
              >
                Filas
              </ModeLabel>
            </ModeSelectorContainer>
          </HeaderContainer>

          <ScrollableContent>
            {viewMode === 'GROUPS' ? (
              <>
                <SectionHeader>MEUS AGRUPAMENTOS</SectionHeader>
                {filteredGroups.length === 0 ? (
                  <div className="text-center text-muted py-2">
                    <small>Nenhum grupo encontrado</small>
                  </div>
                ) : (
                  filteredGroups.map((group) => {
                    const isChecked = selectedGroup?.id === group.id;
                    return (
                      <ListItem
                        key={group.id}
                        onClick={() => handleGroupClick(group)}
                      >
                        <RadioCircle checked={isChecked} />
                        <span>{group.name}</span>
                      </ListItem>
                    );
                  })
                )}
              </>
            ) : (
              <>
                <SectionHeader>MINHAS FILAS</SectionHeader>
                {filteredQueues.length > 0 && (
                  <ListItem onClick={handleSelectAll}>
                    <CheckboxSquare checked={isAllFilteredSelected} />
                    <span style={{ fontWeight: 600 }}>Selecionar todas</span>
                  </ListItem>
                )}
                {filteredQueues.length === 0 ? (
                  <div className="text-center text-muted py-2">
                    <small>Nenhuma fila encontrada</small>
                  </div>
                ) : (
                  filteredQueues.map((queue) => {
                    const isChecked = manualSelection.some(
                      (q) => q.id === queue.id
                    );
                    return (
                      <ListItem
                        key={queue.id}
                        onClick={() => handleQueueClick(queue)}
                      >
                        <CheckboxSquare checked={isChecked} />
                        <span>{queue.name}</span>
                      </ListItem>
                    );
                  })
                )}
              </>
            )}
          </ScrollableContent>

          <Footer onClick={handleOpenManager}>
            <i className="fas fa-info-circle" />
            Mais detalhes
          </Footer>
        </StyledDropdownMenu>
      </Dropdown>

      <MonitoringQueueGroupsManager
        isOpen={isManagerModalOpen}
        toggle={toggleManagerModal}
        onGroupsChanged={onGroupsChanged}
      />
    </>
  );
};
